import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import { getUserEvents } from 'services/user-events';
import { getUnits } from 'services/unit';
import { getLocationsByUnit } from 'services/location';
import MainCard from 'ui-component/cards/MainCard';
import {
    Grid,
    Typography,
    CircularProgress,
    Box,
    Button,
    Stack,
    Chip,
    Card,
    CardContent,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import ViewScheduleModal from '../Calendar/components/ViewScheduleModal';
import UpdateScheduleModal from '../Calendar/components/UpdateScheduleModal';
import DeleteSchedule from '../Calendar/components/DeleteSchedule';
import moment from 'moment';
import 'moment/locale/pt-br';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const MeusAgendamentos = () => {
    const authUser = useSelector((state) => state.auth.user);
    const [agendamentos, setAgendamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scheduleSelected, setScheduleSelected] = useState();
    const [viewScheduleModal, setViewScheduleModal] = useState(false);
    const [updateScheduleModal, setUpdateScheduleModal] = useState(false);
    const [deleteScheduleModal, setDeleteScheduleModal] = useState(false);
    const [updateOptions, setUpdateOptions] = useState({ update_recurrence: false, update_upcoming: false });
    const [deleteOptions, setDeleteOptions] = useState({ cancel_recurrence: false, cancel_upcoming: false });
    const [tabValue, setTabValue] = useState(0);
    const [units, setUnits] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState({});
    const [selectedLocation, setSelectedLocation] = useState({});

    const fetchAgendamentos = () => {
        setLoading(true);
        getUserEvents()
            .then((resp) => {
                // Filtra apenas os agendamentos do usuário logado
                setAgendamentos(resp.data.filter((a) => a.user_id === authUser.id));
            })
            .catch(() => {
                enqueueSnackbar('Erro ao buscar agendamentos', { variant: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const fetchUnits = () => {
        getUnits().then((resp) => {
            setUnits(resp.data.data);
        });
    };

    const fetchLocations = () => {
        if (selectedUnit?.id) {
            getLocationsByUnit(selectedUnit.id).then((resp) => {
                setLocations(resp.data.data);
            });
        } else {
            setLocations([]);
        }
    };

    useEffect(() => {
        fetchAgendamentos();
        fetchUnits();
        // eslint-disable-next-line
    }, [authUser.id]);

    useEffect(() => {
        fetchLocations();
        setSelectedLocation({});
    }, [selectedUnit]);

    const handleView = (agendamento) => {
        setScheduleSelected(agendamento);
        setViewScheduleModal(true);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleUnitChange = (event) => {
        setSelectedUnit(event.target.value);
    };

    const handleLocationChange = (event) => {
        setSelectedLocation(event.target.value);
    };

    const getAgendamentosFiltrados = () => {
        const agora = moment();
        let filtrados = agendamentos;

        // Filtro por data (futuros/encerrados)
        if (tabValue === 0) {
            filtrados = filtrados.filter((a) => moment(a.end).isAfter(agora));
        } else {
            filtrados = filtrados.filter((a) => moment(a.end).isBefore(agora));
        }

        // Filtro por unidade
        if (selectedUnit?.id) {
            filtrados = filtrados.filter((a) => a.location?.unit_id === selectedUnit.id);
        }

        // Filtro por local
        if (selectedLocation?.id) {
            filtrados = filtrados.filter((a) => a.location_id === selectedLocation.id);
        }

        // Ordenar por data de início (mais próximos primeiro para futuros, mais recentes primeiro para encerrados)
        filtrados.sort((a, b) => {
            const dataA = moment(a.start);
            const dataB = moment(b.start);

            if (tabValue === 0) {
                // Agendamentos futuros: mais próximos primeiro
                return dataA.isBefore(dataB) ? -1 : 1;
            } else {
                // Agendamentos encerrados: mais recentes primeiro (ordem decrescente)
                return dataB.isBefore(dataA) ? -1 : 1;
            }
        });

        return filtrados;
    };

    const getScheduleStatus = (agendamento) => {
        if (agendamento?.finished_at) {
            return { label: 'Finalizado', color: 'success' };
        }

        const now = moment();
        const start = moment(agendamento?.start);
        const end = moment(agendamento?.end);

        if (now.isBefore(start)) {
            return { label: 'Aguardando Início', color: 'warning' };
        } else if (now.isAfter(end)) {
            return { label: 'Encerrado', color: 'default' };
        } else {
            return { label: 'Em andamento', color: 'info' };
        }
    };

    const agendamentosFiltrados = getAgendamentosFiltrados();

    return (
        <MainCard>
            <SnackbarProvider>
                <Typography variant="h3" sx={{ mb: 3 }}>
                    Meus Agendamentos
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="filtros de agendamentos">
                        <Tab label="Agendamentos Futuros" />
                        <Tab label="Agendamentos Encerrados" />
                    </Tabs>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="unit-label">Unidade</InputLabel>
                            <Select
                                labelId="unit-label"
                                label="Unidade"
                                value={selectedUnit}
                                onChange={handleUnitChange}
                                renderValue={(value) => value.description || 'Todas as unidades'}
                            >
                                <MenuItem value={{}}>
                                    <em>Todas as unidades</em>
                                </MenuItem>
                                {units?.map((unit) => (
                                    <MenuItem key={unit.id} value={unit}>
                                        {unit.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="location-label">Sala</InputLabel>
                            <Select
                                labelId="location-label"
                                label="Sala"
                                value={selectedLocation}
                                onChange={handleLocationChange}
                                renderValue={(value) => value.description || 'Todas as salas'}
                                disabled={!selectedUnit?.id}
                            >
                                <MenuItem value={{}}>
                                    <em>Todas as salas</em>
                                </MenuItem>
                                {locations?.map((location) => (
                                    <MenuItem key={location.id} value={location}>
                                        {location.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : agendamentosFiltrados.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <Typography variant="body1" color="text.secondary">
                            {tabValue === 0 ? 'Nenhum agendamento futuro encontrado.' : 'Nenhum agendamento encerrado encontrado.'}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {agendamentosFiltrados.map((agendamento) => (
                            <Grid item xs={12} md={6} lg={4} key={agendamento.id}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 3
                                        }
                                    }}
                                    onClick={() => handleView(agendamento)}
                                >
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                    {agendamento.title}
                                                </Typography>
                                                <Chip
                                                    label={getScheduleStatus(agendamento).label}
                                                    color={getScheduleStatus(agendamento).color}
                                                    size="small"
                                                    sx={{ borderRadius: 1 }}
                                                />
                                            </Box>

                                            <Box display="flex" alignItems="center" spacing={1}>
                                                <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {agendamento.location?.description}
                                                </Typography>
                                            </Box>

                                            <Box display="flex" alignItems="center" spacing={1}>
                                                <EventIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {moment(agendamento.start).locale('pt-br').format('DD/MM/YYYY')}
                                                </Typography>
                                            </Box>

                                            <Box display="flex" alignItems="center" spacing={1}>
                                                <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {moment(agendamento.start).locale('pt-br').format('HH:mm')} -{' '}
                                                    {moment(agendamento.end).locale('pt-br').format('HH:mm')}
                                                </Typography>
                                            </Box>

                                            {agendamento.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {agendamento.description}
                                                </Typography>
                                            )}

                                            <Button
                                                variant="text"
                                                color="primary"
                                                size="small"
                                                fullWidth
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleView(agendamento);
                                                }}
                                            >
                                                Visualizar agendamento
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <ViewScheduleModal
                    open={viewScheduleModal}
                    onClose={() => setViewScheduleModal(false)}
                    schedule={scheduleSelected}
                    onUpdateSchedule={() => setUpdateScheduleModal(true)}
                    onDeleteSchedule={() => setDeleteScheduleModal(true)}
                    setUpdateOptions={setUpdateOptions}
                    setDeleteOptions={setDeleteOptions}
                    onFinishSchedule={() => {
                        console.log('onFinishSchedule chamado na tela MeusAgendamentos - fechando modal e atualizando lista');
                        setViewScheduleModal(false);
                        fetchAgendamentos();
                    }}
                />
                <UpdateScheduleModal
                    open={updateScheduleModal}
                    onClose={() => setUpdateScheduleModal(false)}
                    schedule={scheduleSelected}
                    updateOptions={updateOptions}
                    fetchEvents={fetchAgendamentos}
                />
                <DeleteSchedule
                    open={deleteScheduleModal}
                    close={() => setDeleteScheduleModal(false)}
                    schedule={scheduleSelected}
                    deleteOptions={deleteOptions}
                    fetchEvents={fetchAgendamentos}
                />
            </SnackbarProvider>
        </MainCard>
    );
};

export default MeusAgendamentos;
